import { useStore } from "zustand"
import { createStore } from "zustand/vanilla"

export type NotificationFilter = "all" | "unread"

export type NotificationInboxUiState = {
  pageSize: number
  cursor: string | null
  cursorHistory: Array<string | null>
  dialogOpen: boolean
  filter: NotificationFilter
}

export const createNotificationInboxUiStore = () =>
  createStore<NotificationInboxUiState>(() => ({
    pageSize: 20,
    cursor: null,
    cursorHistory: [],
    dialogOpen: false,
    filter: "all",
  }))

export type NotificationInboxUiStore = ReturnType<
  typeof createNotificationInboxUiStore
>

export const notificationInboxStore = createNotificationInboxUiStore()

export const notificationStoreActions = {
  setCursor(cursor: string | null) {
    notificationInboxStore.setState({ cursor })
  },

  setDialogOpen(open: boolean) {
    if (open) {
      notificationInboxStore.setState({
        dialogOpen: true,
        cursor: null,
        cursorHistory: [],
        filter: "all",
      })
    } else {
      notificationInboxStore.setState({ dialogOpen: false })
    }
  },

  setFilter(filter: NotificationFilter) {
    notificationInboxStore.setState({ filter })
  },

  goNextPage(nextCursor: string) {
    const { cursor, cursorHistory } = notificationInboxStore.getState()
    notificationInboxStore.setState({
      cursorHistory: [...cursorHistory, cursor],
      cursor: nextCursor,
    })
  },

  goPreviousPage() {
    const { cursorHistory } = notificationInboxStore.getState()
    if (cursorHistory.length === 0) {
      notificationInboxStore.setState({ cursor: null })
      return
    }

    const nextHistory = cursorHistory.slice(0, -1)
    const previousCursor = cursorHistory.at(-1) ?? null
    notificationInboxStore.setState({
      cursorHistory: nextHistory,
      cursor: previousCursor,
    })
  },
}

export const useNotificationInboxCursor = () =>
  useStore(notificationInboxStore, (s) => s.cursor)

export const useNotificationInboxPageSize = () =>
  useStore(notificationInboxStore, (s) => s.pageSize)

export const useNotificationInboxDialogOpen = () =>
  useStore(notificationInboxStore, (s) => s.dialogOpen)

export const useNotificationInboxFilter = () =>
  useStore(notificationInboxStore, (s) => s.filter)

export const useNotificationInboxCursorHistory = () =>
  useStore(notificationInboxStore, (s) => s.cursorHistory)
